import { App, Divider, Flex, Modal, Skeleton, Table, theme, Tooltip, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { CircleAlert, Copy, FileDown, Pencil, Trash2, LoaderCircle } from 'lucide-react';
import {
  type Key,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { catchError, EMPTY, of, switchMap, tap, throwError } from 'rxjs';

import CustomerOrderCloneModal from './customer-order-clone.modal';
import CustomerOrderView, { orderStatuses } from './customer-order-view.component';
import CustomerOrdersFilter, { type CustomerOrdersFiltersProps } from './customer-orders.filter';
import SitesWithoutOrders from './sites-without-orders.component';
import AuthorizedComponent from '../../../components/authorized-component';
import { DataTable, type DataTableProps } from '../../../components/data-table';
import SyncSuspense from '../../../components/sync-suspense';
import { AuthContext } from '../../../contexts/auth-context-provider';
import ProblemDetailsDto from '../../../dto/error/problem-details-dto';
import { ArchivedCustomerOrder, CustomerOrder } from '../../../entities';
import useArchivedCustomerOrdersList from '../../../hooks/use-archived-customer-orders-list';
import useCustomerOrdersList, {
  type CustomerOrdersListParams,
} from '../../../hooks/use-customer-orders-list';
import useDebouncedState from '../../../hooks/use-debounced-state';
import { useNotification } from '../../../hooks/use-notification';
import useSecuredApiV2 from '../../../hooks/use-secured-api-v2';
import { OrderStatus, Roles } from '../../../types';
import {
  EXPORT_ARCHIVED_CUSTOMER_ORDER_TO_EXCEL,
  EXPORT_CUSTOMER_ORDER_TO_EXCEL,
  CUSTOMER_ORDER_END_POINT_URL,
} from '../../../utils/endpoints';
import { LocalDate } from '../../../utils/local-date';
import { getEarliestOrderDate } from '../../../utils/order-utils';
import utils from '../../../utils/utils';
import type CustomerOrderRevision from '../../../entities/customer-order-revision';
const customerColumn: DataTableProps<CustomerOrder>['columns'][number] = {
  title: 'Customer',
  dataIndex: 'customer',
  render: (value) => (
    <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
      <Typography.Link>{value?.name ?? '-'}</Typography.Link>
    </AuthorizedComponent>
  ),
};

const archivedCustomerColumn: DataTableProps<ArchivedCustomerOrder>['columns'][number] = {
  title: 'Customer',
  dataIndex: 'customer',
  render: (value) => (
    <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
      <Typography.Link>{value?.name ?? '-'}</Typography.Link>
    </AuthorizedComponent>
  ),
};

interface OrdersPageState {
  filters?: CustomerOrdersFiltersProps['initialFilters'];
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD: string = 'id';
const DEFAULT_SORT_DIRECTION: SortOrder = 'descend';
const SUCCESS_CANCEL_MESSAGE = 'Order cancelled successfully';

const reducer = (
  state: OrdersPageState,
  action: {
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?: OrdersPageState['filters'] | OrdersPageState['pagination'] | OrdersPageState['sort'];
  }
): OrdersPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload as OrdersPageState['filters'],
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload as OrdersPageState['pagination'],
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as OrdersPageState['sort'],
      };
  }
};

const archivedReducer = (
  state: OrdersPageState,
  action: {
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?: OrdersPageState['filters'] | OrdersPageState['pagination'] | OrdersPageState['sort'];
  }
): OrdersPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload as OrdersPageState['filters'],
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload as OrdersPageState['pagination'],
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as OrdersPageState['sort'],
      };
  }
};

const editOrderUrl = (id: string): string => {
  return `/update-order/${id}`;
};

const toCustomerOrdersListParams = (
  filters: OrdersPageState['filters'],
  pagination: OrdersPageState['pagination']
): CustomerOrdersListParams => ({
  id: filters?.query && filters.query.length > 0 ? filters.query : undefined,
  orderDateFrom: filters?.orderDate?.[0]?.toISOString(),
  orderDateTo: filters?.orderDate?.[1]?.toISOString(),
  customerIds: filters?.customer,
  siteIds:
    filters?.site == null ? undefined : Array.isArray(filters.site) ? filters.site : [filters.site],
  page: pagination.current,
  size: pagination.pageSize,
});

const CustomerOrdersPage = (): ReactNode => {
  const { modal } = App.useApp();
  const { session } = useContext(AuthContext);
  const notification = useNotification();
  const { token } = theme.useToken();
  const cloneModalRef = useRef<any>(null);
  const [state, setState] = useReducer(reducer, {
    filters: {},
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [archivedState, setArchivedState] = useReducer(archivedReducer, {
    filters: {},
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const navigate = useNavigate();
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const [archivedDebouncedState] = useDebouncedState({ state: archivedState, time: 1000 });
  const [peekedItem, setPeekedItem] = useState<CustomerOrder | ArchivedCustomerOrder | null>(null);
  const peekedRevision = useRef<CustomerOrderRevision['id']>(undefined);
  const securedApiV2 = useSecuredApiV2();
  const customerOrders = useCustomerOrdersList(
    toCustomerOrdersListParams(debouncedState.filters, debouncedState.pagination)
  );
  const archivedCustomerOrders = useArchivedCustomerOrdersList(
    toCustomerOrdersListParams(archivedDebouncedState.filters, archivedDebouncedState.pagination)
  );
  const apiV2 = useSecuredApiV2();

  // Get the order creator's cell phone number for tooltip in Created By column
  const getUserCellPhone = (order: CustomerOrder): string =>
    order.meta?.createdByCellPhone ?? 'No cell phone number available';

  const columns: DataTableProps<CustomerOrder>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'id',
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            peekedRevision.current = item.latestRevisionId;
            setPeekedItem(Object.assign(new CustomerOrder(), item));
          }}
        >
          <strong>#{value}</strong>
        </Typography.Link>
      ),
    },
    {
      title: 'Order date',
      dataIndex: 'orderDate',
      render: (value: CustomerOrder['orderDate']) => (
        <Typography.Text>{value.format('ddd, LL')}</Typography.Text>
      ),
    },
    {
      title: 'Sites',
      dataIndex: ['siteNames'],
      width: 300,
      render: (_, record) => (
        <Flex align="center" gap="small">
          <Typography.Text
            ellipsis={{
              tooltip: (
                <Flex vertical>
                  {(record.siteNamesWithDisplayNumber
                    ? record.siteNamesWithDisplayNumber
                    : record.siteNames
                  )
                    ?.split(', ')
                    .map((siteName) => <Flex key={siteName}>{siteName.trim()}</Flex>)}
                </Flex>
              ),
            }}
            style={{ width: 300 }}
          >
            {record.siteNamesWithDisplayNumber
              ? record.siteNamesWithDisplayNumber
              : record.siteNames}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Status',
      dataIndex: ['status'],
      render: (status) => (
        <Flex gap="small" align="center">
          <Typography.Text>
            {orderStatuses.find((os) => os.value === status)?.label}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Created By',
      dataIndex: ['meta', 'createdBy'],
      render: (_, record) => (
        <Flex align="center" gap="small">
          <Tooltip title={getUserCellPhone(record)}>
            <Typography.Text>{record.meta?.createdBy}</Typography.Text>
          </Tooltip>
        </Flex>
      ),
    },
  ];
  const archivedColumns: DataTableProps<ArchivedCustomerOrder>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'id',
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            peekedRevision.current = undefined;
            setPeekedItem(Object.assign(new ArchivedCustomerOrder(), item));
          }}
        >
          <strong>#{value}</strong>
        </Typography.Link>
      ),
    },
    {
      title: 'Order date',
      dataIndex: 'orderDate',
      render: (value: ArchivedCustomerOrder['orderDate']) => (
        <Typography.Text>{value.format('ddd, LL')}</Typography.Text>
      ),
    },
    {
      title: 'Sites',
      dataIndex: ['siteNames'],
      width: 300,
      render: (_, record) => (
        <Flex align="center" gap="small">
          <Typography.Text
            ellipsis={{
              tooltip: (
                <Flex vertical>
                  {(record.siteNamesWithDisplayNumber
                    ? record.siteNamesWithDisplayNumber
                    : record.siteNames
                  )
                    ?.split(', ')
                    .map((siteName) => <Flex key={siteName}>{siteName.trim()}</Flex>)}
                </Flex>
              ),
            }}
            style={{ width: 300 }}
          >
            {record.siteNamesWithDisplayNumber
              ? record.siteNamesWithDisplayNumber
              : record.siteNames}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Status',
      dataIndex: ['status'],
      render: (status) => (
        <Flex gap="small" align="center">
          <Typography.Text>
            {orderStatuses.find((os) => os.value === status)?.label}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Created By',
      dataIndex: ['originalCreatedBy'],
    },
  ];

  const tableProps = useMemo<Omit<DataTableProps<CustomerOrder>, 'columns'>>(
    () => ({
      title: 'Orders',
      description: 'List of submitted orders',
      filtersNode: (
        <CustomerOrdersFilter
          initialFilters={state.filters}
          onChange={(filters) => {
            setState({ type: 'SET_FILTER', payload: filters });
          }}
          showSiteFilter={session?.claims.role === Roles.ROLE_HHC_ADMIN}
        />
      ),
      actionsNode: <></>,
      dataList: customerOrders.data,
      totalRecords: customerOrders.totalRecords,
      onChange: setState,
      currentPage: state.pagination.current,
      actions: [
        {
          key: 'edit',
          label: 'Edit',
          icon: <Pencil />,
          isDisabled: (record) => {
            return !record.meta?.canUpdate;
          },
          onItemClick: (record) => {
            navigate(editOrderUrl(record.id!));
          },
        },
        {
          key: 'clone',
          label: 'Clone',
          icon: <Copy />,
          onItemClick: (record) => {
            cloneModalRef.current?.updateModal({
              visible: true,
              customerOrder: record,
              date: new LocalDate(getEarliestOrderDate(record.customer.customerSetting)),
            });
          },
        },
        { type: 'divider' },
        {
          key: 'export-xlsx',
          label: 'Export to Excel',
          icon: <FileDown />,
          onItemClick: (record) => {
            exportCustomerOrder(record.id, false);
          },
        },
        { type: 'divider' },
        {
          key: 'cancel',
          label: 'Cancel',
          icon: <Trash2 />,
          danger: true,
          isDisabled: (record) => {
            return !record.meta?.canCancel;
          },
          onItemClick: (record) => {
            void modal.confirm({
              width: '50%',
              title: 'Confirm cancelling order',
              icon: <CircleAlert />,
              content: (
                <Typography.Text>
                  Are you sure you want to cancel order <b>#{record.id}</b>?
                </Typography.Text>
              ),
              okText: 'Yes',
              okType: 'danger',
              cancelText: 'No',
              onOk: () => {
                securedApiV2
                  .patch(
                    `${CUSTOMER_ORDER_END_POINT_URL}/${record.id}/status`,
                    {
                      status:
                        session?.claims.role === Roles.ROLE_HHC_ADMIN
                          ? OrderStatus.CanceledByAdmin
                          : OrderStatus.CanceledByCustomer,
                    },
                    {},
                    {}
                  )
                  .pipe(
                    switchMap((response) =>
                      response.data
                        ? of(response.data)
                        : throwError(() => new ProblemDetailsDto(response))
                    ),
                    tap(() => {
                      notification.success({
                        message: ' ',
                        description: SUCCESS_CANCEL_MESSAGE,
                      });
                      customerOrders.refetch();
                    }),
                    catchError((error: ProblemDetailsDto) => {
                      notification.error({
                        message: ' ',
                        description: error.detail ?? 'Failed to cancel the order',
                      });
                      return EMPTY;
                    })
                  )
                  .subscribe();
              },
            });
          },
        },
      ],
    }),
    [
      customerOrders.data,
      customerOrders.totalRecords,
      customerOrders.errorMessage,
      state.filters,
      state.pagination.current,
    ]
  );

  const archivedTableProps = useMemo<Omit<DataTableProps<ArchivedCustomerOrder>, 'columns'>>(
    () => ({
      title: 'Archived Orders',
      description: 'List of archived orders',
      filtersNode: (
        <CustomerOrdersFilter
          initialFilters={archivedState.filters}
          onChange={(filters) => {
            setArchivedState({ type: 'SET_FILTER', payload: filters });
          }}
        />
      ),
      dataList: archivedCustomerOrders.data,
      totalRecords: archivedCustomerOrders.totalRecords,
      actionsNode: <></>,
      onChange: setArchivedState,
      currentPage: archivedState.pagination.current,
      actions: [
        {
          key: 'clone',
          label: 'Clone',
          icon: <Copy />,
          onItemClick: (record) => {
            cloneModalRef.current?.updateModal({
              visible: true,
              customerOrder: Object.assign(new ArchivedCustomerOrder(), record),
              date: new LocalDate(getEarliestOrderDate(record.customer.customerSetting)),
            });
          },
        },
        { type: 'divider' },
        {
          key: 'export-xlsx',
          label: 'Export to Excel',
          icon: <FileDown />,
          onItemClick: (record) => {
            exportCustomerOrder(record.id, true);
          },
        },
      ],
    }),
    [
      archivedCustomerOrders.data,
      archivedCustomerOrders.totalRecords,
      archivedCustomerOrders.errorMessage,
      archivedState.filters,
      archivedState.pagination.current,
    ]
  );

  useEffect(() => {
    if (peekedItem) {
      setPeekedItem(
        customerOrders.data?.find((customerOrder) => customerOrder.id === peekedItem?.id) ?? null
      );
    }
  }, [customerOrders.data]);

  useEffect(() => {
    customerOrders.errorMessage &&
      notification.error({
        message: ' ',
        description: customerOrders.errorMessage,
      });
  }, [customerOrders.errorMessage]);

  useEffect(() => {
    archivedCustomerOrders.errorMessage &&
      notification.error({
        message: ' ',
        description: archivedCustomerOrders.errorMessage,
      });
  }, [archivedCustomerOrders.errorMessage]);

  const exportCustomerOrder = (
    customerOrderId: CustomerOrder['id'] | ArchivedCustomerOrder['id'],
    isArchived = false
  ): void => {
    const notificationId = utils.randomUUID();
    notification.open({
      key: notificationId,
      message: (
        <Flex vertical>
          <span>{`Exporting order #${customerOrderId}...`}</span>
          <Skeleton
            paragraph={{ style: { display: 'none' } }}
            title={{ style: { width: '100%', marginBlock: token.marginXS } }}
            active
          />
        </Flex>
      ),
      duration: 0,
      icon: <LoaderCircle />,
    });

    const endpoint = isArchived
      ? EXPORT_ARCHIVED_CUSTOMER_ORDER_TO_EXCEL
      : EXPORT_CUSTOMER_ORDER_TO_EXCEL;

    const requestParams = isArchived
      ? { archivedCustomerOrderId: customerOrderId }
      : { customerOrderId };
    apiV2
      .get(endpoint, requestParams, {}, true)
      .pipe(
        tap(({ blob, filename }: { blob: Blob; filename?: string }) => {
          const url = URL.createObjectURL(blob);
          utils.downloadFile(url, filename ?? `order-${customerOrderId}.xlsx`);
          URL.revokeObjectURL(url);
          notification.success({
            key: notificationId,
            description: '',
            message: `Exporting order #${customerOrderId} succeeded`,
          });
        }),
        catchError((error: ProblemDetailsDto) => {
          notification.error({
            key: notificationId,
            description: error.detail ?? '',
            message: `Can't Export Order: ${customerOrderId}`,
          });
          return EMPTY;
        })
      )
      .subscribe();
  };
  const innerColumns = [
    {
      title: 'Version Number',
      dataIndex: 'revisionNumber',
      render: (value: any, item: any) => (
        <Typography.Link
          onClick={() => {
            peekedRevision.current = item.id;
            setPeekedItem(Object.assign(new CustomerOrder(), item.customerOrder));
          }}
        >
          <strong>#{value}</strong>
        </Typography.Link>
      ),
    },
    {
      title: 'Created By',
      dataIndex: ['meta', 'createdBy'],
    },
    {
      title: 'Created At',
      dataIndex: ['meta', 'createdDate'],
      render: (value: any) => (
        <Typography.Text>{value && new Date(value).toLocaleString()}</Typography.Text>
      ),
    },
  ];
  return (
    <Flex gap="large" vertical>
      <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
        <DataTable<CustomerOrder>
          {...tableProps}
          columns={columns.concat([customerColumn])}
          loading={customerOrders.isLoading}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                columns={innerColumns}
                dataSource={
                  record.customerOrderRevisions
                    ?.map((customerOrderRevision) => {
                      customerOrderRevision.customerOrder = record;
                      return customerOrderRevision;
                    })
                    .toSorted(
                      (a, b) =>
                        new Date(b.meta?.createdDate).getTime() -
                        new Date(a.meta?.createdDate).getTime()
                    ) ?? []
                }
                pagination={false}
                size="small"
              />
            ),
            rowExpandable: (record) =>
              record.customerOrderRevisions !== undefined &&
              record.customerOrderRevisions.length > 0,
          }}
        />
      </AuthorizedComponent>
      <AuthorizedComponent roles={[Roles.ROLE_CUSTOMER_ADMIN, Roles.ROLE_CUSTOMER_STANDARD]}>
        <DataTable<CustomerOrder>
          {...tableProps}
          loading={customerOrders.isLoading}
          columns={columns}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                columns={innerColumns}
                dataSource={
                  record.customerOrderRevisions
                    ?.map((customerOrderRevision) => {
                      customerOrderRevision.customerOrder = record;
                      return customerOrderRevision;
                    })
                    .toSorted(
                      (a, b) =>
                        new Date(b.meta?.createdDate).getTime() -
                        new Date(a.meta?.createdDate).getTime()
                    ) ?? []
                }
                pagination={false}
                size="small"
              />
            ),
            rowExpandable: (record) =>
              record.customerOrderRevisions !== undefined &&
              record.customerOrderRevisions.length > 0,
          }}
        />
      </AuthorizedComponent>
      <Divider />
      <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
        <SitesWithoutOrders />
        <Divider />
      </AuthorizedComponent>
      <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
        <DataTable<ArchivedCustomerOrder>
          {...archivedTableProps}
          columns={archivedColumns.concat([archivedCustomerColumn])}
          loading={archivedCustomerOrders.isLoading}
          size="small"
        />
      </AuthorizedComponent>
      <AuthorizedComponent roles={[Roles.ROLE_CUSTOMER_ADMIN, Roles.ROLE_CUSTOMER_STANDARD]}>
        <DataTable<ArchivedCustomerOrder>
          {...archivedTableProps}
          columns={archivedColumns}
          loading={archivedCustomerOrders.isLoading}
          size="small"
        />
      </AuthorizedComponent>
      <SyncSuspense condition={peekedItem}>
        <Modal
          open={!!peekedItem}
          onCancel={() => {
            setPeekedItem(null);
          }}
          cancelButtonProps={{ style: { display: 'none' } }}
          onOk={() => {
            setPeekedItem(null);
          }}
          width={'90vw'}
        >
          <CustomerOrderView
            customerOrder={peekedItem ?? new CustomerOrder()}
            revisionNumber={peekedRevision.current}
            onCustomerOrdersChange={customerOrders.refetch}
            onDismiss={() => {
              setPeekedItem(null);
            }}
          />
        </Modal>
      </SyncSuspense>
      <CustomerOrderCloneModal ref={cloneModalRef} onSuccess={customerOrders.refetch} />
    </Flex>
  );
};

export default CustomerOrdersPage;
